import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Key, 
  LogIn, 
  LogOut, 
  Mail, 
  RefreshCw, 
  Shield, 
  User,
  Clock,
  MapPin,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { activityService, type UserActivityLogItem, type ActivityType, type PaginationMetaData } from '@/services/activity.service';
import SimpleSelect from '@/components/ui/SimpleSelect';

interface ActivityTimelineProps {
  initialLimit?: number;
  showStats?: boolean;
  allowPageSizeChange?: boolean;
}

const getActivityIcon = (activityType: ActivityType, status?: string) => {
  const iconClass = "w-4 h-4";
  
  if (status === 'failed' || status === 'suspicious') {
    return <AlertTriangle className={`${iconClass} text-red-500`} />;
  }
  
  switch (activityType) {
    case 'USER_LOGIN_SUCCESS':
      return <LogIn className={`${iconClass} text-green-500`} />;
    case 'USER_LOGIN_FAILED':
      return <AlertTriangle className={`${iconClass} text-red-500`} />;
    case 'USER_LOGOUT':
      return <LogOut className={`${iconClass} text-gray-500`} />;
    case 'TOKEN_REFRESHED':
      return <RefreshCw className={`${iconClass} text-blue-500`} />;
    case 'PROFILE_UPDATED':
      return <User className={`${iconClass} text-purple-500`} />;
    case 'TOKENS_REVOKED':
      return <Shield className={`${iconClass} text-orange-500`} />;
    case 'PASSWORD_CHANGED':
      return <Key className={`${iconClass} text-green-500`} />;
    case 'PASSWORD_RESET_REQUESTED':
      return <Mail className={`${iconClass} text-yellow-500`} />;
    case 'PASSWORD_RESET_COMPLETED':
      return <CheckCircle className={`${iconClass} text-green-500`} />;
    default:
      return <Activity className={`${iconClass} text-gray-500`} />;
  }
};

const formatDeviceInfo = (userAgent?: string, deviceInfo?: string): string => {
  if (deviceInfo) return deviceInfo;
  if (!userAgent) return 'Unknown Device';
  
  // Simple device detection
  if (userAgent.includes('Mobile') || userAgent.includes('Android') || userAgent.includes('iPhone')) {
    return 'Mobile Device';
  } else if (userAgent.includes('Chrome')) {
    return 'Chrome Browser';
  } else if (userAgent.includes('Firefox')) {
    return 'Firefox Browser';
  } else if (userAgent.includes('Safari')) {
    return 'Safari Browser';
  }
  
  return 'Desktop Browser';
};

export default function ActivityTimeline({ initialLimit = 10, showStats = true, allowPageSizeChange = false }: ActivityTimelineProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  
  // Calculate offset from page number (page is 1-based)
  const offset = (currentPage - 1) * limit;

  // Handle page size change
  const handlePageSizeChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const { data: activityData, isLoading, error, isFetching } = useQuery({
    queryKey: ['userActivity', limit, offset],
    queryFn: () => activityService.getUserActivity({ limit, offset }),
    keepPreviousData: true // Keep previous data while fetching new page
  });

  const activities = activityData?.activities || [];
  const metaData = activityData?.meta_data;
  
  // Calculate total pages
  const totalPages = metaData ? Math.ceil(metaData.total_records / metaData.records_per_page) : 1;
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const { data: stats } = useQuery({
    queryKey: ['activityStats'],
    queryFn: () => activityService.getActivityStats(),
    enabled: showStats
  });

  // Handle page navigation
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Generate page numbers for pagination display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // Maximum visible page numbers
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (isLoading) {
    return (
      <div className="card p-0 overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-0 overflow-hidden">
        <div className="p-4 sm:p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600 font-medium">Failed to load activity timeline</p>
          <p className="text-gray-500 text-sm mt-1">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-0 overflow-hidden">
      <div className="p-4 sm:p-6 border-b">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          {showStats && stats && (
            <div className="flex items-center gap-3 sm:gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>{stats.recentLogins} logins</span>
              </div>
              {stats.failedAttempts > 0 && (
                <div className="flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span>{stats.failedAttempts} failed</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No recent activity to display</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity, index) => (
              <div key={activity.id} className="flex items-start gap-3">
                {/* Activity Icon */}
                <div className="flex-shrink-0 w-8 h-8 bg-gray-50 rounded-full flex items-center justify-center">
                  {getActivityIcon(activity.activity_type, activity.status)}
                </div>

                {/* Activity Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                    <p className="text-sm font-medium text-gray-900">
                      {activityService.formatActivityType(activity.activity_type)}
                    </p>
                    <time className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3" />
                      {activityService.formatRelativeTime(activity.timestamp)}
                    </time>
                  </div>
                  
                  {activity.description && (
                    <p className="text-sm text-gray-600 mt-1 break-words">{activity.description}</p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                    {activity.ip_address && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>{activity.ip_address}</span>
                      </div>
                    )}
                    
                    {(activity.device_info || activity.user_agent) && (
                      <div className="flex items-center gap-1">
                        <Smartphone className="w-3 h-3" />
                        <span>{formatDeviceInfo(activity.user_agent, activity.device_info)}</span>
                      </div>
                    )}
                    
                    {activity.status && activity.status !== 'success' && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        activity.status === 'failed' ? 'bg-red-100 text-red-700' :
                        activity.status === 'suspicious' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {activity.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {activities.length > 0 && metaData && (
        <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* One row from sm up — count left, pages centre, page size right; stacked and centred on phones. */}
          {/* Activity count and pagination info */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
            <p>
              Showing <span className="font-medium text-gray-900">{((currentPage - 1) * limit) + 1}–{Math.min(currentPage * limit, metaData.total_records)}</span> of{' '}
              <span className="font-medium text-gray-900">{metaData.total_records}</span> activities
            </p>
            {isFetching && !isLoading && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" aria-label="Loading" />
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              {/* Previous button */}
              <button
                onClick={goToPrevPage}
                disabled={!hasPrevPage || isFetching}
                className={`p-2 rounded-lg transition-colors ${
                  hasPrevPage && !isFetching
                    ? 'hover:bg-gray-200 text-gray-700'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                        ···
                      </span>
                    );
                  }
                  
                  const pageNum = page as number;
                  const isActive = pageNum === currentPage;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      disabled={isFetching}
                      className={`min-w-[32px] h-8 px-2 rounded-lg transition-colors text-sm font-medium ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'hover:bg-gray-200 text-gray-700'
                      } ${isFetching ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Next button */}
              <button
                onClick={goToNextPage}
                disabled={!hasNextPage || isFetching}
                className={`p-2 rounded-lg transition-colors ${
                  hasNextPage && !isFetching
                    ? 'hover:bg-gray-200 text-gray-700'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Page size selector */}
          {allowPageSizeChange && (
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-600">
              <span>Show</span>
              <div className="w-20">
                <SimpleSelect
                  compact
                  isSearchable={false}
                  value={limit}
                  onChange={(val) => val !== null && handlePageSizeChange(Number(val))}
                  options={[10, 20, 50, 100].map((opt) => ({ label: String(opt), value: opt }))}
                  menuPlacement="auto"
                />
              </div>
              <span>per page</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}